'use client';

import { Button } from '@ahafood/ui/components/button';
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from '@ahafood/ui/components/drawer';
import { Image } from '@ahafood/ui/components/image';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn, formatCurrency } from '@ahm/common-helpers';
import { Check, Loader2, X } from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import { useGetMealPlan } from '@/api/use-get-meal-plan';
import {
  useGetSameItemPlanner,
  type Suggestion,
} from '@/api/use-get-same-item-planner';
import { useUpdateMealPlan } from '@/api/use-update-meal-plan';
import FreeModeSwiper from '@/app/[locale]/(main)/store/components/items/free-mode-swiper';
import { convertMealPlanResponseToStore } from '@/hooks/ai-planner-db-utils';
import { trackEvent } from '@/lib/event-tracking';
import { useAuthStore } from '@/store';
import { useAIPlannerStore, type PlannedMeal } from '@/store/ai-planner';

interface MealSwapSheetProps {
  meal: PlannedMeal | null;
  date: string;
  slot: string;
  isOpen: boolean;
  onClose: () => void;
  onSwap: (newMeal: Suggestion) => void;
}

export function MealSwapSheet({
  meal,
  date,
  slot,
  isOpen,
  onClose,
  onSwap,
}: MealSwapSheetProps) {
  const [selectedItem, setSelectedItem] = useState<Suggestion | null>(null);
  const { user } = useAuthStore();

  const setPlan = useAIPlannerStore.use.setPlan();
  const preferences = useAIPlannerStore.use.preferences();

  // Refetch meal plan from database
  const { refetch: refetchMealPlan } = useGetMealPlan(user?.systemToken ?? '', {
    enabled: false,
  });

  const { mutate: updateMealPlan, isPending: isUpdating } = useUpdateMealPlan(
    user?.systemToken ?? '',
    {
      onSuccess: async () => {
        toast.success('Đã cập nhật món ăn thành công!');

        // Refetch and update plan from database
        const { data } = await refetchMealPlan();
        if (data) {
          const convertedPlan = convertMealPlanResponseToStore(
            data,
            preferences
          );
          setPlan(convertedPlan);
        }
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message ?? 'Có lỗi xảy ra khi cập nhật món ăn'
        );
      },
    }
  );

  const { data, isLoading } = useGetSameItemPlanner(
    {
      name: meal?.name ?? '',
      overall_dish_tag: meal?.overallDishTag ?? '',
      estimated_price: meal?.estimatedPrice ?? 0,
    },
    {
      enabled: isOpen && Boolean(meal?.name && meal?.overallDishTag),
    }
  );

  const handleSelect = useCallback((item: Suggestion) => {
    setSelectedItem(item);
  }, []);

  const handleConfirm = useCallback(() => {
    trackEvent('meal_plan', {
      button_name: 'change_item_meal_plan',
    });
    if (!selectedItem) return;

    updateMealPlan(
      {
        date,
        slot,
        item_name: selectedItem.name,
        overall_dish_tag: selectedItem.overall_dish_tag,
        image_thumb_urls: selectedItem.image_thumb_urls,
        category_solo_meal: selectedItem.category_solo_meal,
        estimated_price: selectedItem.estimated_price,
      },
      {
        onSuccess: () => {
          onSwap(selectedItem);
          setSelectedItem(null);
          onClose();
        },
      }
    );
  }, [selectedItem, date, slot, updateMealPlan, onSwap, onClose]);

  const handleClose = useCallback(() => {
    setSelectedItem(null);
    onClose();
  }, [onClose]);

  return (
    <Drawer open={isOpen} onOpenChange={handleClose}>
      <DrawerContent className="h-auto max-h-[85vh] p-0 bg-[#012749a6] backdrop-blur-md rounded-t-3xl border-0 ai-planner-button">
        <DrawerHeader className="px-4 pt-4 pb-2">
          <div className="flex items-center justify-between">
            <DrawerTitle className="text-white text-lg font-semibold">
              Đổi món ăn
            </DrawerTitle>
            <button
              onClick={handleClose}
              className="p-1.5 rounded-full hover:bg-white/10 text-white/60 hover:text-white transition-colors"
            >
              <X className="size-5" />
            </button>
          </div>
        </DrawerHeader>

        {/* Current Meal */}
        <div className="px-4 pb-3">
          <p className="text-white/60 text-sm mb-2">Món hiện tại:</p>
          <div className="ai-planner-button rounded-xl p-3 border border-white/20">
            <div className="flex items-center gap-3">
              <div className="relative size-16 rounded-lg overflow-hidden shrink-0">
                <Image
                  src={
                    meal?.imageUrl ??
                    `${assetPrefix}/static/images/empty-store-bot.webp`
                  }
                  alt={meal?.name ?? ''}
                  fill
                  className="object-cover"
                />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-white font-semibold text-sm mb-1 line-clamp-1">
                  {meal?.name}
                </h4>
                <p className="text-white/80 text-sm">
                  {formatCurrency(meal?.estimatedPrice ?? 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Suggestions */}
        <div className="px-4 pb-6">
          <p className="text-white/60 text-sm mb-3">
            Chọn món thay thế (vuốt ngang để xem thêm):
          </p>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="size-8 text-white/60 animate-spin" />
            </div>
          ) : (
            <>
              {/* Horizontal Swiper */}
              <FreeModeSwiper
                enableSingMode={true}
                slidesPerView="auto"
                spaceBetween={12}
                className="pb-4 -mx-4 px-4 mb-4"
              >
                {data?.suggestions?.map((item, index) => (
                  <FreeModeSwiper.Slide key={index} className="!w-[90vw]">
                    <button
                      onClick={() => handleSelect(item)}
                      className={cn(
                        'relative w-full ai-planner-button rounded-xl p-3 border transition-all',
                        selectedItem === item
                          ? 'border-[#ff8200] bg-[#ff8200]/10'
                          : 'border-white/20 hover:border-white/40'
                      )}
                    >
                      {/* Selected Indicator */}
                      {selectedItem === item && (
                        <div className="absolute top-2 right-2 size-6 bg-[#ff8200] rounded-full flex items-center justify-center">
                          <Check className="size-4 text-white" />
                        </div>
                      )}

                      <div className="flex items-start gap-3">
                        <div className="relative size-20 rounded-lg overflow-hidden shrink-0">
                          <Image
                            src={
                              item.image_thumb_urls ??
                              `${assetPrefix}/static/images/empty-store-bot.webp`
                            }
                            alt={item.name}
                            fill
                            className="object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0 text-left">
                          <h4 className="text-white font-semibold text-sm mb-1 line-clamp-2">
                            {item.name}
                          </h4>
                          <p className="text-white/80 text-sm font-medium mb-1">
                            {formatCurrency(item.estimated_price)}
                          </p>
                        </div>
                      </div>
                    </button>
                  </FreeModeSwiper.Slide>
                ))}
              </FreeModeSwiper>

              {/* Confirm Button */}
              <Button
                onClick={handleConfirm}
                disabled={!selectedItem || isUpdating}
                className="w-full bg-gradient-to-r from-[#ff8200] to-[#ff6b00] hover:from-[#ff8200]/90 hover:to-[#ff6b00]/90 text-white font-semibold rounded-xl h-12 text-base shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="size-4 mr-2 animate-spin" />
                    Đang cập nhật...
                  </>
                ) : (
                  'Xác nhận đổi món'
                )}
              </Button>
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
